/**
 * Deep object merging utilities
 *
 * Provides functions for deeply merging objects with customizable behavior,
 * useful for configuration management, state updates, and data composition.
 */

import { DEEP_MERGE } from "./constants";

export type DeepPartial<T> = T extends object
  ? {
      [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
    }
  : T;

export interface DeepMergeOptions {
  /**
   * Whether to clone arrays instead of merging them
   * @default DEEP_MERGE.DEFAULT_CLONE_ARRAYS (false)
   */
  cloneArrays?: boolean;

  /**
   * Whether to merge arrays by concatenating them
   * When false, source arrays replace target arrays
   * @default DEEP_MERGE.DEFAULT_MERGE_ARRAYS (false)
   */
  mergeArrays?: boolean;

  /**
   * Maximum depth for nested object merging to prevent infinite recursion
   * @default DEEP_MERGE.DEFAULT_MAX_DEPTH (10)
   */
  maxDepth?: number;

  /**
   * Array of field names to skip during merge
   * @default []
   */
  skipFields?: string[];

  /**
   * Custom merge function for specific values
   * Return undefined to use default merge behavior
   */
  customMerger?: (target: unknown, source: unknown, key: string) => unknown;
}

/**
 * Deeply merges two or more objects, with later objects taking precedence.
 * Type-safe version that preserves object structure.
 *
 * @param target - The base object to merge into
 * @param sources - One or more source objects to merge
 * @param options - Configuration options for the merge
 * @returns A new object with all properties merged
 *
 * @example
 * ```typescript
 * const defaults = { name: 'App', settings: { theme: 'dark', lang: 'en' } }
 * const overrides = { settings: { theme: 'light' } }
 *
 * const merged = deepMerge(defaults, overrides)
 * // Returns: { name: 'App', settings: { theme: 'light', lang: 'en' } }
 * ```
 *
 * @example
 * ```typescript
 * // Merge multiple objects
 * const config = deepMerge(
 *   { api: { timeout: 5000, retries: 3 } },
 *   { api: { timeout: 10000 } },
 *   { api: { endpoint: '/v2' } }
 * )
 * // Returns: { api: { timeout: 10000, retries: 3, endpoint: '/v2' } }
 * ```
 *
 * @example
 * ```typescript
 * // Merge arrays by concatenation
 * const merged = deepMerge(
 *   { items: [1, 2] },
 *   { items: [3, 4] },
 *   { mergeArrays: true }
 * )
 * // Returns: { items: [1, 2, 3, 4] }
 * ```
 */
export function deepMerge<T extends Record<string, unknown>, O extends T | DeepPartial<T>>(
  target: T,
  other: O,
  options?: DeepMergeOptions
): T {
  // Separate sources from options
  const sources: Record<string, unknown>[] = [];

  if (other === undefined || other === null) {
    return target;
  }
  // Check if this is an options object by looking for option-specific keys
  if (
    typeof other === "object" &&
    ("cloneArrays" in other ||
      "mergeArrays" in other ||
      "maxDepth" in other ||
      "skipFields" in other ||
      "customMerger" in other)
  ) {
    options = other as DeepMergeOptions;
  } else if (typeof other === "object") {
    sources.push(other as Record<string, unknown>);
  }

  return deepMergeInternal(target, sources, options ?? {}, 0) as T;
}

/**
 * Checks if a value is a plain object (not an array, Date, etc.)
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") {
    return false;
  }

  // Check if it's a plain object (not an array, Date, or other special object)
  return (
    (value as Record<string, unknown>).constructor === Object ||
    (value as Record<string, unknown>).constructor === undefined
  );
}

/**
 * Deeply clones a value
 */
function deepClone<T>(value: T): T {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (value instanceof Date) {
    return new Date(value.getTime()) as T;
  }

  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item)) as T;
  }

  if (isPlainObject(value)) {
    const cloned: Record<string, unknown> = {};
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        cloned[key] = deepClone(value[key]);
      }
    }
    return cloned as T;
  }

  // For other object types (Map, Set, custom classes), return as-is
  return value;
}

function deepMergeInternal(
  target: Record<string, unknown>,
  sources: Array<Record<string, unknown>>,
  options: DeepMergeOptions,
  depth: number
): Record<string, unknown> {
  const {
    cloneArrays = DEEP_MERGE.DEFAULT_CLONE_ARRAYS,
    mergeArrays = DEEP_MERGE.DEFAULT_MERGE_ARRAYS,
    maxDepth = DEEP_MERGE.DEFAULT_MAX_DEPTH,
    skipFields = [],
    customMerger,
  } = options;

  // Prevent infinite recursion
  if (depth > maxDepth) {
    return target;
  }

  // Start with a clone of the target if it's a plain object
  const result = isPlainObject(target) ? { ...target } : target;

  // Merge each source into the result
  for (const source of sources) {
    if (source === null || typeof source !== "object") {
      continue;
    }

    // Iterate through all keys in the source
    for (const key in source) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) {
        continue;
      }

      // Skip fields if specified
      if (skipFields.includes(key)) {
        continue;
      }

      const targetValue = result[key];
      const sourceValue = source[key];

      // Use custom merger if provided
      if (customMerger) {
        const customResult = customMerger(targetValue, sourceValue, key);
        if (customResult !== undefined) {
          result[key] = customResult;
          continue;
        }
      }

      // Handle undefined source values - skip them (don't overwrite with undefined)
      if (sourceValue === undefined) {
        continue;
      }

      // Handle null source values - explicitly set to null
      if (sourceValue === null) {
        result[key] = null;
        continue;
      }

      // Handle arrays
      if (Array.isArray(sourceValue)) {
        if (mergeArrays && Array.isArray(targetValue)) {
          // Concatenate arrays
          result[key] = [...targetValue, ...sourceValue];
        } else if (cloneArrays) {
          // Clone the array
          result[key] = [...sourceValue];
        } else {
          // Replace with source array
          result[key] = sourceValue;
        }
        continue;
      }

      // Handle plain objects - merge recursively
      if (isPlainObject(sourceValue)) {
        if (isPlainObject(targetValue)) {
          // Both are plain objects - merge recursively
          result[key] = deepMergeInternal(targetValue, [sourceValue], options, depth + 1);
        } else {
          // Target is not a plain object - clone source
          result[key] = deepClone(sourceValue);
        }
        continue;
      }

      // For all other types (primitives, Date, RegExp, etc.), use source value
      result[key] = sourceValue;
    }
  }

  return result;
}
